import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErrorMessage, photoUrl } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2, MessageCircle, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

function formatWhen(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString(lang === "es" ? "es-DO" : "en-US", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString(lang === "es" ? "es-DO" : "en-US", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

export default function Messages() {
  const { t, lang, formatPrice } = useLocale();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c") || null;

  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);

  const active = useMemo(
    () => conversations.find((c) => c.conversation_id === activeId),
    [conversations, activeId]
  );

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMessages(data);
      await api.post(`/conversations/${id}/read`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);

  // Polling while a conversation is open
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/conversations/${activeId}/messages`);
        setMessages((prev) => (prev.length !== data.length ? data : prev));
        loadConversations();
      } catch (err) {
        // silent polling error
      }
    }, 5000);
    return () => clearInterval(t);
  }, [activeId, loadConversations]);

  // Auto-scroll to bottom
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, activeId]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { body });
      setMessages((prev) => [...prev, data]);
      setDraft("");
      loadConversations();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const openConv = (id) => {
    const next = new URLSearchParams(params);
    next.set("c", id);
    setParams(next);
  };

  const backToList = () => {
    const next = new URLSearchParams(params);
    next.delete("c");
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-10 pt-6 md:pt-14 pb-16">
      <div className="mb-6 md:mb-8">
        <div className="label-eyebrow">{t("nav.messages")}</div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl">{t("chat.inbox_title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("chat.inbox_sub")}</p>
      </div>

      <div className="grid md:grid-cols-[340px,1fr] gap-4 border border-border rounded-lg overflow-hidden bg-card min-h-[70vh]">
        {/* Conversation list */}
        <aside
          className={`border-r border-border ${active ? "hidden md:block" : "block"}`}
          data-testid="conversations-list"
        >
          {loadingConvs ? (
            <div className="p-8 text-center text-muted-foreground text-sm">{t("common.loading")}</div>
          ) : conversations.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">
              <MessageCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
              {t("chat.empty_inbox")}
            </div>
          ) : (
            <ul>
              {conversations.map((c) => {
                const isActive = c.conversation_id === activeId;
                const unread = c.unread_count || 0;
                return (
                  <li key={c.conversation_id}>
                    <button
                      onClick={() => openConv(c.conversation_id)}
                      data-testid={`conv-item-${c.conversation_id}`}
                      className={`w-full text-left px-4 py-4 border-b border-border transition-colors flex gap-3 ${
                        isActive ? "bg-muted" : "hover:bg-muted/60"
                      }`}
                    >
                      <div className="h-11 w-11 shrink-0 rounded-full overflow-hidden bg-muted">
                        {c.property?.photos?.[0] ? (
                          <img src={photoUrl(c.property.photos[0])} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                            {c.counterpart?.name?.[0]?.toUpperCase() || "?"}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{c.counterpart?.name || c.counterpart?.email}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{formatWhen(c.last_message_at || c.created_at, lang)}</span>
                        </div>
                        <div className="text-xs text-primary/80 truncate mt-0.5">{c.property?.title || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate mt-1">
                          {c.last_message || "—"}
                        </div>
                      </div>
                      {unread > 0 && (
                        <span
                          className="self-center inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold"
                          data-testid={`unread-badge-${c.conversation_id}`}
                        >
                          {unread}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Chat panel */}
        <section
          className={`flex flex-col ${active ? "flex" : "hidden md:flex"}`}
          data-testid="chat-panel"
        >
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-8 text-center">
              <div>
                <MessageCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
                {t("chat.empty_selected")}
              </div>
            </div>
          ) : (
            <>
              <header className="px-4 py-3 border-b border-border flex items-center gap-3">
                <button
                  className="md:hidden text-muted-foreground"
                  onClick={backToList}
                  data-testid="chat-back"
                  aria-label="Volver"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="h-10 w-10 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                  {active.counterpart?.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{active.counterpart?.name}</div>
                  {active.property && (
                    <Link
                      to={`/properties/${active.property.property_id}`}
                      className="text-xs text-primary hover:underline underline-offset-4 truncate block"
                      data-testid="chat-property-link"
                    >
                      {active.property.title} · {formatPrice(active.property.price, active.property.currency)}
                    </Link>
                  )}
                </div>
              </header>

              <div className="flex-1 overflow-y-auto px-4 py-6 space-y-3 bg-muted/30" data-testid="messages-scroll">
                {loadingMsgs && messages.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-12">{t("common.loading")}</div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                            mine
                              ? "bg-primary text-primary-foreground rounded-br-sm"
                              : "bg-background border border-border text-foreground rounded-bl-sm"
                          }`}
                          data-testid={`msg-${m.message_id}`}
                        >
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                            {formatWhen(m.created_at, lang)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={listEndRef} />
              </div>

              <form onSubmit={send} className="p-3 border-t border-border flex items-center gap-2" data-testid="chat-form">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t("chat.placeholder")}
                  autoFocus
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !draft.trim()} className="rounded-full" data-testid="chat-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
